/**
 * Parser for the proof. annotation grammar.
 *
 * Converts raw source text into a DocumentNode AST.
 *
 * Grammar rules handled here:
 * - Markdown headings => ScopeHeadingNode (scope tracking via ScopeStack)
 * - @keyword args => SectionBlockNode, DirectiveNode, ScaleDirectiveNode,
 *                    BindingNode, DefinitionNode, DefineSectionNode
 * - @keyword(...) => ParenDirectiveNode (or paren-form section open)
 * - @ref{…} (legacy) => ParenDirectiveNode with paren form (round-trip rewrite)
 * - label: value inside a section => LabelledLineNode
 *   - value starting with `=` => ExpressionNode
 *   - value with `@scale:` suffix => ScalingRuleNode
 *   - value with `(@N.NN g/ml)` hint => DensityHintNode
 * - // block-comment HTML-comment => CommentNode
 * - blank lines => BlankLineNode
 * - everything else => ProseLineNode
 *
 * Track 2.A constructs (@bind, @define, @define-section, expressions) are
 * handled via the specialised sub-parsers in ./bindings.ts, ./definitions.ts,
 * ./scaling.ts, ./expressions.ts.
 *
 * Pure TS -- no DOM, no Node built-ins.
 */

import type {
  DocumentNode,
  TopLevelNode,
  SectionBlockNode,
  ScopeHeadingNode,
  DirectiveNode,
  ParenDirectiveNode,
  CommentNode,
  BlankLineNode,
  ProseLineNode,
  LabelledLineNode,
  RatingValue,
  ProseValueSpan,
  InlineMathSpan,
  ScopePath,
  ExpressionNode,
  ScalingRuleNode,
  DensityHintNode,
  BindingNode,
  DefinitionNode,
  DefineSectionNode,
  Span,
} from "../ast/types.js";
import { RESERVED_DIRECTIVES, INLINE_MATH_RE, PROSE_VALUE_RE } from "../lexer/index.js";
import { ScopeStack } from "./scope.js";
import { parseBinding, tryParseAsExpression } from "./bindings.js";
import { parseDefinition, parseSectionDefinition } from "./definitions.js";
import { parseScaleDirective, stripScaleSuffix, parseScalingRuleSuffix } from "./scaling.js";
import { parseExpression } from "./expressions.js";

// ---------------------------------------------------------------------------
// Block UUID extraction
// ---------------------------------------------------------------------------

const BLOCK_UUID_SUFFIX_RE =
  /\s+#b-([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\s*$/u;

function extractBlockUuid(text: string): { clean: string; uuid: string | undefined } {
  const m = BLOCK_UUID_SUFFIX_RE.exec(text);
  if (!m) return { clean: text, uuid: undefined };
  return {
    clean: text.slice(0, m.index).trimEnd(),
    uuid: m[1] ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Legacy @ref{…} → @ref(…) rewrite
// ---------------------------------------------------------------------------

/**
 * Rewrite legacy `@ref{content}` to `@ref(content)`.
 * This is the Track 2.A alias rule: parsed and round-tripped as paren form.
 */
function rewriteLegacyRef(line: string): string {
  return line.replace(/@ref\{([^}]*)\}/gu, "@ref($1)");
}

// ---------------------------------------------------------------------------
// Rating value inference
// ---------------------------------------------------------------------------

function inferRatingValue(raw: string): RatingValue {
  const trimmed = raw.trim();

  // Numeric: N or N/M
  const numericMatch = /^(\d+(?:\.\d+)?)(?:\/(\d+(?:\.\d+)?))?$/u.exec(trimmed);
  if (numericMatch) {
    const numerator = parseFloat(numericMatch[1] ?? "0");
    const denominator = numericMatch[2] !== undefined ? parseFloat(numericMatch[2]) : 5;
    return { raw, inferredType: "stars", numerator, denominator };
  }

  // Boolean
  if (/^(?:yes|no|y|n)$/iu.test(trimmed)) {
    const booleanValue = /^(?:yes|y)$/iu.test(trimmed);
    return { raw, inferredType: "boolean", booleanValue };
  }

  return { raw, inferredType: "text" };
}

// ---------------------------------------------------------------------------
// Prose value auto-detection
// ---------------------------------------------------------------------------

function detectProseValues(text: string): ProseValueSpan[] {
  const spans: ProseValueSpan[] = [];
  const re = new RegExp(PROSE_VALUE_RE.source, "gu");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const numericStr = m[1] ?? "";
    const unit = m[2] ?? "";
    if (numericStr !== "") {
      spans.push({ kind: "prose-value", raw: m[0], numericStr, unit });
    }
  }
  return spans;
}

// ---------------------------------------------------------------------------
// Inline math detection
// ---------------------------------------------------------------------------

function detectInlineMath(raw: string): InlineMathSpan | undefined {
  if (INLINE_MATH_RE.test(raw)) {
    return { kind: "inline-math", raw };
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Density hint detection: `(@N.NN g/ml)` suffix on value strings
// ---------------------------------------------------------------------------

const DENSITY_HINT_RE = /\s*\(@\s*(\d+(?:\.\d+)?)\s*g\/ml\)/u;

function extractDensityHint(raw: string): {
  cleanValue: string;
  hint: DensityHintNode | undefined;
} {
  const m = DENSITY_HINT_RE.exec(raw);
  if (m === null) return { cleanValue: raw, hint: undefined };
  const density = parseFloat(m[1] ?? "0");
  const hint: DensityHintNode = {
    kind: "density-hint",
    density,
    raw: m[0].trim(),
  };
  return {
    cleanValue: raw.slice(0, m.index).trim(),
    hint,
  };
}

// ---------------------------------------------------------------------------
// Comment detection helpers
// ---------------------------------------------------------------------------

function isLineComment(line: string): boolean {
  return /^(\s*)\/\//u.test(line);
}

function isBlockCommentStart(line: string): boolean {
  return /^\s*\/\*/u.test(line);
}

function isHtmlCommentStart(line: string): boolean {
  return /^\s*<!--/u.test(line);
}

// ---------------------------------------------------------------------------
// Parse line as @keyword rest
// ---------------------------------------------------------------------------

interface AtLineResult {
  keyword: string;
  rest: string;
}

const AT_LINE_RE = /^(\s*)@([a-zA-Z][a-zA-Z0-9_-]*)(.*)$/u;

function parseAtLine(line: string): AtLineResult | null {
  const m = AT_LINE_RE.exec(line);
  if (!m) return null;
  // Do NOT trimStart here -- we need the space before #b-<uuid> for UUID extraction.
  return { keyword: m[2] ?? "", rest: m[3] ?? "" };
}

// ---------------------------------------------------------------------------
// Known paren-directive keywords (always inline, never open a section block)
// ---------------------------------------------------------------------------

/**
 * These keywords always form inline paren-directives, never section blocks.
 * @ref, @consumes, @produces are always inline annotations.
 * @observe and @section can open blocks (paren-form section headers).
 */
const PAREN_DIRECTIVE_KEYWORDS = new Set(["ref", "consumes", "produces"]);

// ---------------------------------------------------------------------------
// Content-bearing inline keywords (QA 2026-06-11 bug A5)
// ---------------------------------------------------------------------------

/**
 * Keywords whose inline trailing text IS the item content, not a section
 * disambiguation label.
 *
 * `@plan-next Push hydration to 80%.` means the sentence is the plan item
 * (the inline form documented in client/parse-plan-items.ts), unlike
 * `@ingredients dry` where "dry" labels the component. For these keywords the
 * parser emits the trailing text as a prose-line child so every AST consumer
 * (canonical renderer, read view, seed-next-version) renders it as body
 * content by construction — never folded into a chrome heading.
 *
 * `@intent` (A-WP4) joins this set: `@intent less yeast, longer cold ferment`
 * is this-version's hypothesis, frozen WITH the version and stripped from the
 * successor draft by seedNextVersion. It is an inert annotation — no evaluator
 * or Values-panel involvement (extensibility-design.md §12; Track A decision A1).
 */
export const CONTENT_INLINE_KEYWORDS: ReadonlySet<string> = new Set([
  "plan-next",
  "todo",
  "intent",
]);

/**
 * Content-inline keywords whose block carries AT MOST ONE line of content — the
 * annotation is single-line by design. `@intent` (A-WP4) is the only such
 * keyword: it is this-version's one-line hypothesis (multi-line intent is out of
 * scope). For these the parser makes the section self-limiting so it can never
 * swallow subsequent unrelated prose:
 *   - inline form (`@intent less yeast`) is emitted self-contained (the section
 *     never stays open), mirroring the client scanner in parse-plan-items.ts;
 *   - block form (`@intent` then the text on the next line) closes the moment it
 *     receives its first non-blank content child.
 * Without this, an `@intent` written above method prose would absorb that prose
 * into its section-block, and because seedNextVersion DROPS the intent block the
 * swallowed method text would be silently deleted from the successor draft.
 * Unlike `@intent`, `@plan-next` / `@todo` are legitimately multi-line blocks and
 * are carried by the seed transform, so they stay open (their pre-existing
 * behaviour is unchanged).
 */
export const SINGLE_LINE_CONTENT_KEYWORDS: ReadonlySet<string> = new Set(["intent"]);

// ---------------------------------------------------------------------------
// Track 2.A: keywords that open top-level grammar constructs (not sections)
// ---------------------------------------------------------------------------

/**
 * These `@keyword` lines are Track 2.A grammar constructs, not section blocks.
 * They are handled before the generic section-block path.
 */
const GRAMMAR_CONSTRUCT_KEYWORDS = new Set(["bind", "define", "define-section"]);

// ---------------------------------------------------------------------------
// Parse heading
// ---------------------------------------------------------------------------

const HEADING_RE = /^(#{1,6})\s+(.+)$/u;

interface HeadingResult {
  level: number;
  text: string;
}

function parseHeading(line: string): HeadingResult | null {
  const m = HEADING_RE.exec(line);
  if (!m) return null;
  return { level: (m[1] ?? "").length, text: m[2] ?? "" };
}

// ---------------------------------------------------------------------------
// Parse labelled line (label: value)
// ---------------------------------------------------------------------------

interface LabelledResult {
  label: string;
  rawValue: string;
}

function parseLabelledLine(line: string): LabelledResult | null {
  // Must have at least one char before :, and not be a URL.
  for (let i = 1; i < line.length; i++) {
    if (line[i] === ":") {
      // Skip ://
      if (line[i + 1] === "/" && line[i + 2] === "/") continue;
      const label = line.slice(0, i).trim();
      const rawValue = line.slice(i + 1).trim();
      if (label.length > 0) {
        return { label, rawValue };
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Expression parsing in value slots
// ---------------------------------------------------------------------------

/**
 * Try to parse a labelled-line value as an expression.
 *
 * Handles two forms (per extensibility-design.md §3.1 and §3.4.1):
 *
 * 1. `=expr` — explicit expression prefix (canonical form per §3.1).
 * 2. `expr=` — trailing-`=` form (user-friendly shorthand; user types
 *    `hydration: water/flour=` meaning "compute this"). Stripped to `expr`
 *    and parsed as an expression. A "hint" ParseError is emitted by the
 *    caller to suggest the canonical form without the trailing `=`.
 *
 * Returns `{ node, trailingEqHint }` so the caller can add the hint
 * diagnostic without needing to re-detect the trailing-`=` case.
 *
 * Note: bare expression auto-detection (§3.4.1 Form 3: `hydration: water/flour`
 * without any `=`) is intentionally excluded here. That form is spec-defined
 * but could collide with inline-math spans (`100g/2=50g`) and other plain-text
 * values. The explicit `=` prefix (Form 1) is the safe authoring path. The
 * trailing-`=` shorthand (Form 2) bridges the UX gap. The full auto-detection
 * logic lives in `bindings.ts:tryParseAsExpression` for future wiring.
 */
interface TryParseExpressionResult {
  node: ExpressionNode | undefined;
  /**
   * True when the trailing-`=` form was detected. The caller should
   * emit a hint ParseError suggesting canonical syntax.
   */
  trailingEqHint: boolean;
  /**
   * Set when an explicit `=expr` form is incomplete (a dangling operator such as
   * `=water +`). The user asked for a formula (Form 1) but it does not resolve
   * to a whole expression. The caller attaches this to the LabelledLineNode so
   * the evaluator produces a `grammar-invalid` error value instead of silently
   * evaluating the partial left-hand side (P3.EDITOR item 5).
   */
  exprError?: string;
}

/**
 * Parser errors that mean "the expression is not complete" — a binary operator
 * with no right-hand side, or a truncated / empty expression. These are the
 * signals that a Form-1 `=expr` should surface as an incomplete-expression
 * error rather than falling back to the partial left-hand side.
 */
const INCOMPLETE_EXPR_ERROR_RE =
  /Expected expression after|Unexpected token: eof|Empty expression/u;

function tryParseExpression(rawValue: string): TryParseExpressionResult {
  const trimmed = rawValue.trim();

  // Form 1 — explicit `=expr` prefix (canonical per §3.1)
  if (trimmed.startsWith("=")) {
    const result = parseExpression(trimmed);
    // The user explicitly opened a formula. If the parse is incomplete (e.g. a
    // trailing operator `=water +`), do NOT silently keep the partial left-hand
    // side — flag it so the evaluator shows `→?` "Expression is incomplete."
    if (result.errors.some((e) => INCOMPLETE_EXPR_ERROR_RE.test(e))) {
      return {
        node: undefined,
        trailingEqHint: false,
        exprError: "Expression is incomplete.",
      };
    }
    return {
      node: result.node ?? undefined,
      trailingEqHint: false,
    };
  }

  // Form 2 — trailing `=` (e.g. `water/flour=`)
  // The `=` at end is not a comparison operator in this context;
  // the user intends it as "compute / show result". Strip it and
  // parse the remainder as an expression.
  //
  // Guard: skip if the candidate before `=` looks like a plain numeric literal
  // (e.g. `400g=`). We do not want to treat a stray `=` after a literal value
  // as a "derived value" marker.
  if (trimmed.endsWith("=") && trimmed.length > 1) {
    const candidate = trimmed.slice(0, -1).trim();
    if (!/^\d+(?:\.\d+)?(?:[a-zA-Z%°][a-zA-Z0-9%°]*)?$/u.test(candidate)) {
      const result = parseExpression("=" + candidate);
      if (result.node !== null) {
        return { node: result.node, trailingEqHint: true };
      }
    }
  }

  // Form 3 — implicit expression auto-detection (extensibility-design.md §3.4.1
  // / L11 / GH #191).
  //
  // RHS is treated as an expression when it contains an arithmetic operator
  // (`+`, `-`, `*`, `/`) AND does not match the safer literal / inline-math
  // patterns we already preserve verbatim.
  //
  // Guards (must skip these to preserve existing behaviour):
  //   - Plain literal: `400g`, `65%`, `100`, `200°C` — covered by
  //     `tryParseAsExpression`'s internal literal check.
  //   - Inline math span (`100g/2=50g`) — preserve as literal so the editor
  //     can keep the user's hand-shown math visible. Detected via
  //     INLINE_MATH_RE (presence of trailing `= …` in the value).
  //   - Bare prose ("see book", "spread evenly") — has no arithmetic op so
  //     stays a literal even though spaced identifiers are valid references.
  //
  // The "Hydration: flour/water" case lands here: contains `/`, references
  // alphabetic identifiers, not a plain literal, not inline math → parsed as
  // a `flour / water` BinaryOp ExpressionNode. The graph builder will then
  // resolve `flour` and `water` to their declared values (literals or
  // @define), and unresolved refs surface as `undefined-reference` errors via
  // the existing evaluator path (no new eval surface — closed sandbox).
  if (INLINE_MATH_RE.test(trimmed)) {
    return { node: undefined, trailingEqHint: false };
  }

  // Require at least one arithmetic operator OR parens-wrapped expression
  // before attempting parse. Without this guard, bare prose like "see book"
  // would parse as a single spaced-identifier reference and get auto-bound.
  if (!HAS_ARITHMETIC_OP_RE.test(trimmed) && !PARENS_WRAPPED_EXPR_RE.test(trimmed)) {
    return { node: undefined, trailingEqHint: false };
  }

  const implicitNode = tryParseAsExpression(trimmed);
  if (implicitNode !== null) {
    return { node: implicitNode, trailingEqHint: false };
  }

  return { node: undefined, trailingEqHint: false };
}

// ---------------------------------------------------------------------------
// Position helper
// ---------------------------------------------------------------------------

/**
 * Build a single-line Span for a given 1-based line number.
 *
 * The parser walks the source line-by-line so `column` precision is not
 * available at this level — the lexer has it for syntax-highlighting, but
 * the AST only needs `start.line` for the Values-panel → editor flashLine
 * seam. `end` is set identical to `start`; callers that need accurate column
 * ranges should use lexer tokens instead.
 *
 * All node kinds produced directly in the main parse loop populate `span`
 * uniformly (LabelledLineNode, BindingNode, ScopeHeadingNode, SectionBlockNode,
 * DirectiveNode, DefinitionNode, DefineSectionNode, ParenDirectiveNode,
 * CommentNode, BlankLineNode, ProseLineNode).
 * Expression sub-nodes (LiteralNode, ReferenceNode, BinaryOpNode, …) do not
 * carry spans — they are not addressable source lines and the map does not
 * need them.
 */
function lineSpan(lineNo: number): Span {
  const pos = { line: lineNo, column: 0 };
  return { start: pos, end: pos };
}

// Auto-bind heuristic: arithmetic operator anywhere in the value.
// Hyphens in identifiers like `oven-temp` are not counted because the
// negative-prose check requires a space-bounded operator (`a - b`) — bare
// hyphens within a word stay literal. `*` and `/` must not be inside a
// number-with-unit literal pattern, which is handled by the
// `tryParseAsExpression` literal-skip check below.
const HAS_ARITHMETIC_OP_RE =
  /(?:^|[^a-zA-Z0-9])[+*/](?:[^a-zA-Z0-9]|$)|[a-zA-Z0-9]\s*[+*/]\s*[a-zA-Z0-9]|[a-zA-Z0-9]\s+-\s+[a-zA-Z0-9]/u;

// Parens-wrapped expression: `(a + b)`, `(flour / total)`. Heuristic-only;
// `tryParseAsExpression` still validates the body.
const PARENS_WRAPPED_EXPR_RE = /^\s*\(.*\)\s*$/u;

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

export interface ParseResult {
  ast: DocumentNode;
  errors: ParseError[];
}

export interface ParseError {
  line: number;
  message: string;
  /**
   * Severity of the parse diagnostic.
   *
   * - `"error"` — hard error that prevents evaluation of the affected line.
   * - `"hint"` — soft diagnostic; evaluation proceeds with the parser's best
   *   interpretation. Use to guide the user toward canonical syntax.
   *
   * Hard errors from `@bind` / `@define` parse failures default to `"error"`.
   * Trailing-`=` labelled-line diagnostics default to `"hint"`.
   *
   * Consumers: the editor surfaces `"hint"` diagnostics as inline suggestions;
   * the Outline ERRORS card shows only evaluator errors (not ParseErrors) and
   * is unaffected by this field. See extensibility-design.md §3.4.1.
   */
  severity: "error" | "hint";
}

/**
 * Parse source into a DocumentNode AST.
 *
 * The parser is a single-pass line-by-line state machine.
 * It handles:
 * - Scope accumulation from markdown headings.
 * - Section block open/close (next @section, next heading, @end, EOF).
 * - Labelled lines inside sections, including Track 2.A constructs.
 * - Comments, blank lines, and prose.
 * - Track 2.A: @bind, @define, @define-section, @scale (extended),
 *   expressions in value slots, @scale: suffix, density hints,
 *   legacy @ref{} → @ref() rewrite.
 */
export function parse(source: string): ParseResult {
  // Apply legacy @ref{} → @ref() rewrite across the entire source first
  const rewritten = rewriteLegacyRef(source);

  const lines = rewritten.split("\n");
  const errors: ParseError[] = [];
  const scopeStack = new ScopeStack();
  const children: TopLevelNode[] = [];

  let lineIdx = 0;
  let openSection: SectionBlockNode | null = null;

  function closeSection(): void {
    if (openSection !== null) {
      children.push(openSection);
      openSection = null;
    }
  }

  /**
   * Attach a Track 2.A grammar-construct node (@bind / @define / @define-section)
   * to the document in source order WITHOUT terminating the enclosing section.
   *
   * These directives are NOT section openers. When one appears inside an open
   * section it nests as a section child (just like an inline `@ref(...)`
   * paren-directive already does), so the section stays open and every
   * subsequent `label: value` line keeps evaluating. When one appears at the top
   * level (no open section — the common case, e.g. `@bind` above `@ingredients`)
   * it lands as a top-level child unchanged.
   *
   * Before this helper, the @bind / @define branches called `closeSection()`,
   * which orphaned the following labelled lines as prose (P0 swallow, dev
   * 2026-07-02). See design.md §7.7 and extensibility-design.md §5.3.
   */
  function attachConstruct(node: BindingNode | DefinitionNode | DefineSectionNode): void {
    if (openSection !== null) {
      openSection.children.push(node);
    } else {
      children.push(node);
    }
  }

  function currentScope(): ScopePath {
    return scopeStack.current();
  }

  function addError(msg: string, severity: ParseError["severity"] = "error"): void {
    errors.push({ line: lineIdx + 1, message: msg, severity });
  }

  while (lineIdx < lines.length) {
    const line = lines[lineIdx] ?? "";

    // ----- Multi-line block comment -----
    if (isBlockCommentStart(line)) {
      const startLineNo = lineIdx + 1; // 1-based
      let combined = line;
      let endIdx = lineIdx;
      while (endIdx < lines.length && !combined.includes("*/", combined.indexOf("/*") + 2)) {
        endIdx++;
        combined += "\n" + (lines[endIdx] ?? "");
      }
      const raw = combined.slice(combined.indexOf("/*"), combined.indexOf("*/") + 2);
      const node: CommentNode = {
        kind: "comment",
        form: "block",
        raw,
        span: lineSpan(startLineNo),
      };
      if (openSection !== null) {
        openSection.children.push(node);
      } else {
        children.push(node);
      }
      lineIdx = endIdx + 1;
      continue;
    }

    // ----- Multi-line HTML comment -----
    if (isHtmlCommentStart(line)) {
      const startLineNo = lineIdx + 1; // 1-based
      let combined = line;
      let endIdx = lineIdx;
      while (endIdx < lines.length && !combined.includes("-->", combined.indexOf("<!--") + 4)) {
        endIdx++;
        combined += "\n" + (lines[endIdx] ?? "");
      }
      const openPos = combined.indexOf("<!--");
      const closePos = combined.indexOf("-->", openPos + 4);
      const raw = combined.slice(openPos, closePos + 3);
      const node: CommentNode = { kind: "comment", form: "html", raw, span: lineSpan(startLineNo) };
      if (openSection !== null) {
        openSection.children.push(node);
      } else {
        children.push(node);
      }
      lineIdx = endIdx + 1;
      continue;
    }

    // ----- Line comment -----
    if (isLineComment(line)) {
      const raw = line.slice(line.indexOf("//"));
      const node: CommentNode = { kind: "comment", form: "line", raw, span: lineSpan(lineIdx + 1) };
      if (openSection !== null) {
        openSection.children.push(node);
      } else {
        children.push(node);
      }
      lineIdx++;
      continue;
    }

    // ----- Blank line -----
    if (/^\s*$/u.test(line)) {
      const node: BlankLineNode = { kind: "blank-line", span: lineSpan(lineIdx + 1) };
      if (openSection !== null) {
        openSection.children.push(node);
      } else {
        children.push(node);
      }
      lineIdx++;
      continue;
    }

    // ----- Markdown heading (scope) -----
    const heading = parseHeading(line);
    if (heading !== null) {
      closeSection();
      scopeStack.push(heading.level, heading.text);
      const node: ScopeHeadingNode = {
        kind: "scope-heading",
        level: heading.level,
        text: heading.text,
        scopePath: currentScope(),
        span: lineSpan(lineIdx + 1),
      };
      children.push(node);
      lineIdx++;
      continue;
    }

    // ----- @ line -----
    const atLine = parseAtLine(line);
    if (atLine !== null) {
      const { keyword, rest } = atLine;
      const restTrimmed = rest.trimStart();

      // ---- Track 2.A: @bind (directive — does NOT close the section) ----
      if (keyword === "bind") {
        const result = parseBinding(restTrimmed, currentScope());
        if (result.node !== null) {
          result.node.span = lineSpan(lineIdx + 1);
          attachConstruct(result.node);
        } else {
          for (const e of result.errors) addError(e);
        }
        lineIdx++;
        continue;
      }

      // ---- Track 2.A: @define-section (directive — does NOT close the section) ----
      if (keyword === "define-section") {
        const result = parseSectionDefinition(restTrimmed, currentScope());
        if (result.node !== null) {
          result.node.span = lineSpan(lineIdx + 1);
          attachConstruct(result.node);
        } else {
          for (const e of result.errors) addError(e);
        }
        lineIdx++;
        continue;
      }

      // ---- Track 2.A: @define (directive — does NOT close the section) ----
      if (keyword === "define") {
        const result = parseDefinition(restTrimmed, currentScope());
        if (result.node !== null) {
          result.node.span = lineSpan(lineIdx + 1);
          attachConstruct(result.node);
        } else {
          for (const e of result.errors) addError(e);
        }
        lineIdx++;
        continue;
      }

      // ---- Directive (reserved keyword) ----
      if (RESERVED_DIRECTIVES.has(keyword)) {
        closeSection();

        // Track 2.A: Extended @scale directive
        if (keyword === "scale") {
          const scaleArgs = restTrimmed.trim();

          // Only promote to ScaleDirectiveNode for the extended Track 2.A forms:
          // factor forms (`@scale x1.5`, `@scale 1.5x`, bare `@scale 1.5`) and
          // geometry (`@scale from=... to=...`). Anything starting with a
          // multiplier marker or a digit is treated as an attempted factor so
          // garbage like `1.5xx` surfaces an invalid-factor error, not silence.
          // The legacy `/2` division form stays as DirectiveNode for backward
          // compat — it is inert in the evaluator (ground truth #2).
          if (/^(?:from=|[x×0-9.])/u.test(scaleArgs)) {
            const scaleResult = parseScaleDirective(scaleArgs, currentScope());
            for (const e of scaleResult.errors) addError(e);
            if (scaleResult.node !== null) {
              scaleResult.node.span = lineSpan(lineIdx + 1);
              children.push(scaleResult.node);
            }
            lineIdx++;
            continue;
          }
        }

        const args = restTrimmed.trim() || undefined;
        const node: DirectiveNode = {
          kind: "directive",
          name: keyword,
          ...(args !== undefined ? { args } : {}),
          scopePath: currentScope(),
          span: lineSpan(lineIdx + 1),
        };
        children.push(node);
        lineIdx++;
        continue;
      }

      // ---- Known inline paren-directive keywords: @ref(...), @consumes(...), @produces(...) ----
      if (PAREN_DIRECTIVE_KEYWORDS.has(keyword) && restTrimmed.startsWith("(")) {
        const closeParenIdx = findMatchingParen(restTrimmed, 0);
        const parenContent =
          closeParenIdx >= 0 ? restTrimmed.slice(1, closeParenIdx) : restTrimmed.slice(1);
        const node: ParenDirectiveNode = {
          kind: "paren-directive",
          name: keyword,
          parenContent,
          scopePath: currentScope(),
          span: lineSpan(lineIdx + 1),
        };
        if (openSection !== null) {
          openSection.children.push(node);
        } else {
          children.push(node);
        }
        lineIdx++;
        continue;
      }

      // ---- Paren-form section block open: @keyword(...) -- opens a block ----
      if (restTrimmed.startsWith("(")) {
        closeSection();
        const closeParenIdx = findMatchingParen(restTrimmed, 0);
        const parenContent =
          closeParenIdx >= 0 ? restTrimmed.slice(1, closeParenIdx) : restTrimmed.slice(1);

        // Check if there is a #b-<uuid> suffix after the closing paren
        const afterParen = closeParenIdx >= 0 ? restTrimmed.slice(closeParenIdx + 1) : "";
        const { uuid } = extractBlockUuid(afterParen.length > 0 ? afterParen : "");

        const newSection: SectionBlockNode = {
          kind: "section-block",
          keyword,
          parenArgs: parenContent,
          ...(uuid !== undefined ? { blockUuid: uuid } : {}),
          scopePath: currentScope(),
          children: [],
          span: lineSpan(lineIdx + 1),
        };
        openSection = newSection;
        lineIdx++;
        continue;
      }

      // ---- Plain section header: @keyword [inline-label] [#b-uuid] ----
      // Use the un-trimmed rest for UUID extraction (need the space before #b-)
      closeSection();
      const { clean: cleanRest, uuid } = extractBlockUuid(rest);
      const trimmedClean = cleanRest.trim();

      // QA 2026-06-11 bug A5: for content-bearing keywords (@plan-next, @todo)
      // the trailing inline text is item CONTENT, not a disambiguation label —
      // emit it as a prose-line child so consumers render it as body text.
      const isContentInline = CONTENT_INLINE_KEYWORDS.has(keyword) && trimmedClean.length > 0;

      const newSection: SectionBlockNode = {
        kind: "section-block",
        keyword,
        ...(trimmedClean.length > 0 && !isContentInline ? { inlineLabel: trimmedClean } : {}),
        ...(uuid !== undefined ? { blockUuid: uuid } : {}),
        scopePath: currentScope(),
        children: [],
        span: lineSpan(lineIdx + 1),
      };
      if (isContentInline) {
        newSection.children.push({
          kind: "prose-line",
          text: trimmedClean,
          proseValues: detectProseValues(trimmedClean),
          span: lineSpan(lineIdx + 1),
        });
        // Single-line content-inline keywords (@intent) are self-contained in
        // the inline form: emit the completed block at top level and leave no
        // open section, so following prose is never swallowed (and therefore
        // never dropped with the block by seedNextVersion). Matches the client
        // scanner, where an inline @keyword line does not open a block.
        if (SINGLE_LINE_CONTENT_KEYWORDS.has(keyword)) {
          children.push(newSection);
          lineIdx++;
          continue;
        }
      }
      openSection = newSection;
      lineIdx++;
      continue;
    }

    // ----- Labelled line (inside section) -----
    const labelled = parseLabelledLine(line);
    if (labelled !== null && openSection !== null) {
      const { label, rawValue: rawValueRaw } = labelled;

      // Strip @scale: suffix first
      const { cleanValue: afterScaleStrip, scaleSuffix } = stripScaleSuffix(rawValueRaw);

      // Extract density hint
      const { cleanValue: afterDensityStrip, hint: densityHint } =
        extractDensityHint(afterScaleStrip);

      const rawValue = afterDensityStrip;

      // Try to parse the value as an expression (three forms — see tryParseExpression).
      const exprResult = tryParseExpression(rawValue);
      const expression = exprResult.node;

      // Trailing-`=` hint: the user typed `label: expr=` instead of `label: expr`.
      // Emit a soft hint so the editor can suggest the canonical form.
      // The expression is still evaluated — evaluation is not blocked by this hint.
      if (exprResult.trailingEqHint) {
        const canonical = rawValue.trim().slice(0, -1).trim();
        addError(
          `Trailing '=' detected on derived value. Use \`${label}: ${canonical}\` (the '=' is not needed; the expression is auto-detected).`,
          "hint",
        );
      }

      // Incomplete explicit formula (e.g. `label: =water +`): surface a hard
      // parse error AND carry `exprError` onto the node so the evaluator resolves
      // it to a `grammar-invalid` value (`→?`) instead of the partial LHS
      // (P3.EDITOR item 5).
      if (exprResult.exprError !== undefined) {
        addError(exprResult.exprError);
      }

      // Parse inline math (only if not an expression)
      const inlineMath = expression !== undefined ? undefined : detectInlineMath(rawValue);

      // Parse scaling rule suffix
      let scalingRule: ScalingRuleNode | undefined;
      if (scaleSuffix !== undefined) {
        const scaleResult = parseScalingRuleSuffix(scaleSuffix);
        if (scaleResult.rule !== null) {
          scalingRule = scaleResult.rule;
        } else {
          for (const e of scaleResult.errors) addError(e);
        }
      }

      const proseValues = detectProseValues(rawValue);
      const isRatingSection = openSection.keyword === "rating";
      const node: LabelledLineNode = {
        kind: "labelled-line",
        label,
        rawValue,
        ...(inlineMath !== undefined ? { inlineMath } : {}),
        proseValues,
        ...(isRatingSection ? { ratingValue: inferRatingValue(rawValue) } : {}),
        ...(expression !== undefined ? { expression } : {}),
        ...(exprResult.exprError !== undefined ? { exprError: exprResult.exprError } : {}),
        ...(scalingRule !== undefined ? { scalingRule } : {}),
        ...(densityHint !== undefined ? { densityHint } : {}),
        scopePath: openSection.scopePath,
        sectionKeyword: openSection.keyword,
        span: lineSpan(lineIdx + 1),
      };
      openSection.children.push(node);
      // Single-line content-inline section (@intent) took its one content line
      // in block form — close it so it cannot absorb further prose.
      if (SINGLE_LINE_CONTENT_KEYWORDS.has(openSection.keyword)) {
        closeSection();
      }
      lineIdx++;
      continue;
    }

    // ----- Prose line -----
    const proseValues = detectProseValues(line);
    const proseNode: ProseLineNode = {
      kind: "prose-line",
      text: line,
      proseValues,
      span: lineSpan(lineIdx + 1),
    };
    if (openSection !== null) {
      openSection.children.push(proseNode);
      // Single-line content-inline section (@intent) took its one content line
      // in block form — close it so following prose is never swallowed (and thus
      // never deleted with the block on seed).
      if (SINGLE_LINE_CONTENT_KEYWORDS.has(openSection.keyword)) {
        closeSection();
      }
    } else {
      children.push(proseNode);
    }
    lineIdx++;
  }

  // Close any open section at EOF
  closeSection();

  const ast: DocumentNode = {
    kind: "document",
    children,
    span: { start: { line: 1, column: 0 }, end: { line: lines.length, column: 0 } },
  };
  return { ast, errors };
}

// ---------------------------------------------------------------------------
// Utility: find matching closing paren
// ---------------------------------------------------------------------------

function findMatchingParen(text: string, openIdx: number): number {
  let depth = 0;
  for (let i = openIdx; i < text.length; i++) {
    if (text[i] === "(") depth++;
    else if (text[i] === ")") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

// Re-export for backward compat
export { GRAMMAR_CONSTRUCT_KEYWORDS };
